import type { ReactNode } from "react";
import styled from "styled-components";
import { FormGroup } from "../../../components/FormGroup";
import { FormLabel } from "../../../components/FormLabel";
import { Input } from "../../../components/Input";
import { SelectInput } from "../../../components/SelectInput";
import { Toggle } from "../../../components/Toggle";
import { useGetIntegrationsQuery } from "../../../query/integrationsQuery";
import type { IntegrationType } from "@KassaroAB/shared/types/enums/IntegrationType";
import type { ChainId, IntegrationId } from "@KassaroAB/shared/types/ids";
import colors from "@styling/colors";
import { fontSizes } from "@styling/fonts";
import { spacing } from "@styling/sizing";

export const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const FieldError = styled.span`
  color: ${colors.danger};
  font-size: ${fontSizes.small}px;
`;

const fieldErrorText = (errors: unknown[]): string | null => {
  const first = errors[0];
  return typeof first === "string" ? first : null;
};

type FieldRowProps = {
  label: string;
  errors?: unknown[];
  children: ReactNode;
};

export const FieldRow = ({ label, errors = [], children }: FieldRowProps) => {
  const error = fieldErrorText(errors);
  return (
    <FormGroup>
      <FormLabel>{label}</FormLabel>
      {children}
      {error && <FieldError>{error}</FieldError>}
    </FormGroup>
  );
};

type TextFieldProps = {
  label: string;
  value: string | undefined;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  inputMode?: "numeric" | "text";
  errors?: unknown[];
  disabled?: boolean;
};

export const TextField = ({
  label,
  value,
  onChange,
  onBlur,
  placeholder,
  inputMode,
  errors,
  disabled,
}: TextFieldProps) => (
  <FieldRow label={label} errors={errors}>
    <Input
      type="text"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
      inputMode={inputMode}
      disabled={disabled}
    />
  </FieldRow>
);

type ToggleFieldProps = {
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
  onBlur?: () => void;
};

export const ToggleField = ({ label, value, onChange, onBlur }: ToggleFieldProps) => (
  <FieldRow label={label}>
    <Toggle checked={value} onChange={onChange} onBlur={onBlur} />
  </FieldRow>
);

type IntegrationFieldProps = {
  label: string;
  placeholder: string;
  chainId: ChainId;
  integrationTypes: IntegrationType[];
  value: IntegrationId | "";
  onChange: (value: IntegrationId) => void;
  onBlur?: () => void;
  errors?: unknown[];
};

export const IntegrationField = ({
  label,
  placeholder,
  chainId,
  integrationTypes,
  value,
  onChange,
  onBlur,
  errors,
}: IntegrationFieldProps) => {
  const { data: integrations } = useGetIntegrationsQuery(chainId);
  const options =
    integrations?.filter((integration) =>
      integrationTypes.includes(integration.type),
    ) ?? [];
  return (
    <FieldRow label={label} errors={errors}>
      <SelectInput
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
      >
        <option value="">{placeholder}</option>
        {options.map((integration) => (
          <option key={integration.id} value={integration.id}>
            {integration.name} ({integration.type})
          </option>
        ))}
      </SelectInput>
    </FieldRow>
  );
};
